import React, { useEffect, useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { attachPeekStream } from "./videoMediaPresence";
import { getProcessorOutputCanvas, startProcessorPreviewCopy } from "./videoBackground";

/**
 * Ampliação da câmera no piso: o tile continua no tapete; aqui só há uma cópia do stream.
 */
export default function VoFloorCamPeek({
  name = "",
  cargo = "",
  mirrored = false,
  track = null,
  onClose,
}) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    const wrap = wrapRef.current;
    if (!el || !track) return undefined;
    const stopStream = attachPeekStream(track, el);
    el.style.transform = mirrored ? "scaleX(-1)" : "";
    const srcCanvas = getProcessorOutputCanvas(track);
    let stopCopy = null;
    if (srcCanvas && wrap) {
      const overlay = wrap.ownerDocument.createElement("canvas");
      overlay.setAttribute("data-video-bg-preview", "1");
      Object.assign(overlay.style, {
        position: "absolute",
        inset: "0",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        pointerEvents: "none",
        transform: mirrored ? "scaleX(-1)" : "",
      });
      stopCopy = startProcessorPreviewCopy(track, overlay, {
        onFrame: () => {
          if (overlay.isConnected) el.style.opacity = "0";
        },
      });
      if (stopCopy) wrap.appendChild(overlay);
    }
    return () => {
      stopCopy?.();
      stopStream?.();
      el.style.opacity = "";
      wrap?.querySelectorAll?.("canvas[data-video-bg-preview]")?.forEach((node) => node.remove());
    };
  }, [track, mirrored]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const caption = [name, cargo].filter(Boolean).join(" · ");

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={caption || "Câmera ampliada"}
      data-vo-cam-peek="1"
      onClick={onClose}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, md: 3 },
        bgcolor: "rgba(16,10,8,0.72)",
        cursor: "zoom-out",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "relative",
          width: "min(920px, 96%)",
          aspectRatio: "16 / 9",
          maxHeight: "82%",
          borderRadius: "18px",
          overflow: "hidden",
          bgcolor: "#000",
          boxShadow: "0 28px 80px rgba(0,0,0,0.55)",
          cursor: "default",
        }}
      >
        <Box ref={wrapRef} sx={{ position: "absolute", inset: 0 }}>
          <video
            ref={videoRef}
            playsInline
            autoPlay
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", background: "#000" }}
          />
        </Box>
        {caption ? (
          <Typography
            sx={{
              position: "absolute",
              left: 14,
              bottom: 14,
              zIndex: 2,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "rgba(32,33,36,0.82)",
              color: "#f8f4ee",
              fontSize: "0.92rem",
              fontWeight: 700,
              maxWidth: "78%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        ) : null}
        <IconButton
          aria-label="Fechar câmera ampliada"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            color: "#fff",
            bgcolor: "rgba(32,33,36,0.72)",
            "&:hover": { bgcolor: "rgba(60,64,67,0.92)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
